<script setup lang="ts">
import { cva, type VariantProps } from "class-variance-authority";
import { twMerge } from "tailwind-merge";
import { computed } from "vue";

/**
 * SHAPE is its own axis, and TEXT is a variant.
 *
 * `pill` is a boolean beside `block` rather than a fifth variant because a pill is
 * a shape, not a colour: the app has primary pills and ghost pills, and folding it
 * into `variant` would force every rounded button to be the same colour.
 *
 * `link` is a variant, because it IS a colour — but it also has to shed the box,
 * and `size` is declared after it, so the height comes back. The compound below
 * runs last and is what actually undoes it.
 *
 * ── POR QUE O `secondary` USA A BORDA FORTE (09/09/2026) ──────────────────
 *
 * Ele desenhava `border-border` sobre `bg-surface`, e as duas coisas juntas
 * fazem um botão branco com um contorno de 1,21:1 numa página quase branca:
 * o texto lia-se, o BOTÃO não — "Ajustar este agente" parecia uma legenda, e o
 * dono relatou-o como invisível na tela de Agentes. No escuro é o mesmo
 * (1,38:1), pelo mesmo motivo.
 *
 * `border-strong` existe exactamente para isto e o próprio `tokens.css` o diz:
 * é o que um CONTROLO mostra, e tem de limpar 3:1 contra toda superfície em que
 * possa assentar (WCAG 1.4.11). Medido depois da troca: 3,31:1 sobre a página e
 * 3,45:1 sobre o próprio fundo do botão no claro; 3,78:1 e 3,49:1 no escuro.
 *
 * O que NÃO muda é o peso: o fundo continua a ser a superfície e o texto
 * continua a ser `fg`. Um secundário continua a ler-se como secundário — passa
 * apenas a ter uma aresta que existe.
 *
 * ── PADRÃO CLAUDE (dono, 25/10/2026) ──────────────────────────────────────
 *
 * Todo botão é pílula (`rounded-full`); o `pill` fica como prop para não
 * quebrar quem o passa. O secundário flutua: `surface-raised` + `shadow-sm`
 * sobre a hairline, e é o degrau de superfície que o separa da página. O botão
 * de ícone é redondo, quieto e de 40px (alvo de toque).
 */
const button = cva(
	"inline-flex items-center justify-center gap-2 rounded-full font-medium transition-colors disabled:cursor-not-allowed",
	{
		variants: {
			variant: {
				// Cheio e desligado vira cinza neutro: o azul a 50% parecia botão quebrado (dono, 25/10).
				primary:
					"bg-accent text-fg-on-accent hover:bg-accent-hover disabled:bg-bg-muted disabled:text-fg-subtle",
				secondary:
					"border border-border bg-surface-raised text-fg shadow-sm hover:bg-bg-muted disabled:opacity-50",
				ghost: "text-fg-muted hover:bg-bg-muted hover:text-fg disabled:opacity-50",
				danger:
					"bg-danger text-fg-on-accent hover:opacity-90 disabled:bg-bg-muted disabled:text-fg-subtle disabled:opacity-100",
				link: "text-accent underline-offset-2 hover:underline disabled:opacity-50",
			},
			size: {
				xs: "h-7 px-2 text-xs",
				sm: "h-8 px-3 text-xs",
				md: "h-10 px-4 text-sm",
				lg: "h-11 px-5 text-sm",
				/** Square, for a lone glyph: padding on a square is what makes it a rectangle. */
				icon: "size-10 p-0",
			},
			pill: { true: "rounded-full", false: "" },
			block: { true: "w-full", false: "" },
		},
		compoundVariants: [
			{ variant: "link", class: "h-auto p-0" },
			// O ícone fantasma é quieto: meia-luz até ser apontado.
			{ variant: "ghost", size: "icon", class: "text-fg-subtle" },
		],
		defaultVariants: { variant: "primary", size: "md", pill: false, block: false },
	},
);

type Props = {
	variant?: NonNullable<VariantProps<typeof button>["variant"]>;
	size?: NonNullable<VariantProps<typeof button>["size"]>;
	pill?: boolean;
	block?: boolean;
	type?: "button" | "submit";
	disabled?: boolean;
	loading?: boolean;
	class?: string;
};
const props = withDefaults(defineProps<Props>(), { type: "button" });

/**
 * ── NUM BOTÃO DE ÍCONE, O GIRO TOMA O LUGAR DO ÍCONE (25/09/2026) ─────────
 *
 * Num botão com texto o giro vai AO LADO do rótulo — "⟳ Salvar" continua a
 * dizer o que está a acontecer. Num `size="icon"` não há rótulo: há um glifo
 * num quadrado de 32px, e giro + glifo + `gap-2` não cabem nele. O enviar do
 * inbox mostrava os dois lado a lado enquanto a mensagem (ou o vídeo) subia —
 * dois elementos onde devia haver um. Aqui o giro SUBSTITUI o glifo até o
 * `loading` cair, e o botão volta a ser o mesmo avião de papel.
 */
const glyphOnly = computed(() => props.size === "icon");
const classes = computed(() =>
	twMerge(
		button({ variant: props.variant, size: props.size, pill: props.pill, block: props.block }),
		props.class,
	),
);
</script>

<template>
	<button :type="type" :class="classes" :disabled="disabled || loading">
		<span
			v-if="loading"
			class="size-3.5 animate-spin rounded-full border-2 border-current border-t-transparent"
			aria-hidden="true"
		/>
		<slot v-if="!(loading && glyphOnly)" />
	</button>
</template>
